import React, { useCallback, useEffect, useRef } from "react";
import { classnames, Icon, PlainButton } from "@culturehq/components";
import { useLazyImport } from "@culturehq/hooks";
import { Trans } from "app/utils";
import { PartialPromptQuestion, PromptQuestionOmitId } from "chq";
import AIQuestionOption, { AIQuestionOptionProps } from "./AIQuestionOption";
import styles from "./newAIStoryQuestionModal.module.scss";

interface Props {
  questions: PartialPromptQuestion[];
  filteredQuestions: PartialPromptQuestion[];
  Sortable?: any;
  onDelete: AIQuestionOptionProps["onDelete"];
  onUpdate: AIQuestionOptionProps["onUpdate"];
  onRefresh: () => void;
  onSortQuestions: (sortedQuestions: PartialPromptQuestion[]) => void;
}

const QuestionOptionsInner: React.FC<Props> = ({
  questions,
  filteredQuestions,
  Sortable,
  onDelete,
  onUpdate,
  onRefresh,
  onSortQuestions
}) => {
  const questionsRef = useRef(null);

  const handleSort = useCallback((event: any) => {
    const currentQuestions = [...questions];
    const questionElements = Array.from(event.to.children as HTMLCollection);
    questionElements.forEach((optionElement, position) => {
      const index = optionElement.getAttribute("data-id");
      const found = currentQuestions[Number(index)];
      found.position = position;
    });
    onSortQuestions(currentQuestions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (Sortable) {
      const sortableInstance = new Sortable(questionsRef.current, {
        handle: ".sortable__drag-handler",
        onEnd: handleSort
      });

      return () => sortableInstance.destroy();
    }
  }, [Sortable, handleSort]);

  return (
    <div className={styles.chart}>
      <div className={classnames("chq-ffd", styles["chart-header"])}>
        <span className={classnames("chq-ffd--lb", styles.title)}>
          <Trans>Questions </Trans>
          <div className={styles.disclaimer}><Trans>(min 2, max 5)</Trans></div>
        </span>
        <PlainButton
          className={styles["refresh-questions"]}
          onClick={onRefresh}
        >
          <Icon icon="refresh" /><Trans>Refresh questions</Trans>
        </PlainButton>
      </div>
      <div ref={questionsRef}>
        {filteredQuestions.map((question, index) => (
          <AIQuestionOption
            index={index}
            question={question as PromptQuestionOmitId}
            questionsLength={filteredQuestions.length}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </div>
  );
};

const AIQuestionOptions: React.FC<Props> = ({
  questions,
  filteredQuestions,
  onDelete,
  onUpdate,
  onRefresh,
  onSortQuestions
}) => {
  const Sortable = useLazyImport(() => import("sortablejs"));

  return Sortable
    ? (
      <QuestionOptionsInner
        questions={questions}
        filteredQuestions={filteredQuestions}
        Sortable={Sortable}
        onDelete={onDelete}
        onUpdate={onUpdate}
        onRefresh={onRefresh}
        onSortQuestions={onSortQuestions}
      />
    ) : null;
};

export default AIQuestionOptions;
